import { CheckOutlined } from '@ant-design/icons';
import { useTheme } from '@material-ui/core';
import { passwordRules } from '@portal/config/app.json';
import { translate } from '@portal/services/i18n';
import {
  checkPasswordValidation,
  PasswordValidation,
  validatePassword,
} from '@portal/utils/validate';
import React from 'react';
import ThemedText from '@portal/components/ThemedText/ThemedText';

type PasswordCheckProps = {
  password: string;
  passwordValidation: PasswordValidation;
};

const PasswordCheck = ({
  password,
  passwordValidation,
}: PasswordCheckProps) => {
  const { palette } = useTheme();

  return (
    <>
      <div className="password-check__card">
        {!checkPasswordValidation(validatePassword(password ?? '')) ? (
          <ThemedText variant="h5">
            {translate(
              'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.PASSWORD_WEAK'
            )}
          </ThemedText>
        ) : (
          <ThemedText variant="h5">
            {translate(
              'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.SECURITY'
            )}
          </ThemedText>
        )}
        <div className="password-check text-start">
          {passwordRules.atLeastOneLetter && passwordRules.atLeastOneNumber ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.letter && passwordValidation.number
                  ? 'valid'
                  : 'default'
              }`}
            >
              {passwordValidation.letter && passwordValidation.number ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.LETTER_NUMBER'
              )?.replace('{{letter}}', `${passwordRules.atLeastOneLetter}`)}
            </ThemedText>
          ) : null}
          {passwordRules.atLeastOneLetter && !passwordRules.atLeastOneNumber ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.letter ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.letter ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.LETTER'
              )?.replace('{{letter}}', `${passwordRules.atLeastOneLetter}`)}
            </ThemedText>
          ) : null}
          {passwordRules.atLeastOneNumber && !passwordRules.atLeastOneLetter ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.number ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.number ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.NUMBER'
              )}
            </ThemedText>
          ) : null}
          {passwordRules.minLength && passwordRules.maxLength ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.min ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.min ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.MIN_MAX'
              )
                ?.replace('{{minLength}}', `${passwordRules.minLength}`)
                .replace('{{maxLength}}', `${passwordRules.maxLength}`)}
            </ThemedText>
          ) : null}
          {passwordRules.minLength && !passwordRules.maxLength ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.min ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.min ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.MIN'
              )?.replace('{{minLength}}', `${passwordRules.minLength}`)}
            </ThemedText>
          ) : null}
          {passwordRules.maxLength && !passwordRules.minLength ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.max ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.max ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.MAX'
              )?.replace('{{maxLength}}', `${passwordRules.maxLength}`)}
            </ThemedText>
          ) : null}
          {passwordValidation?.uppercase !== undefined &&
          passwordRules.upperCase &&
          !passwordRules.lowerCase ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.uppercase ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.uppercase ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.UPPERCASE'
              )}
            </ThemedText>
          ) : null}
          {passwordRules.lowerCase && !passwordRules.lowerCase ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.lowercase ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.lowercase ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.LOWERCASE'
              )}
            </ThemedText>
          ) : null}
          {passwordRules.lowerCase && passwordRules.upperCase ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.lowercase && passwordValidation.uppercase
                  ? 'valid'
                  : 'default'
              }`}
            >
              {passwordValidation.lowercase && passwordValidation.uppercase ? (
                <CheckOutlined color={palette.primary.main} className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.UPPERCASE_LOWERCASE'
              )}
            </ThemedText>
          ) : null}
          {passwordValidation?.special !== undefined ? (
            <ThemedText
              variant="h5"
              className={`password-check__${
                passwordValidation.special ? 'valid' : 'default'
              }`}
            >
              {passwordValidation.special ? (
                <CheckOutlined className="mr-1" />
              ) : null}
              {translate(
                'PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.FORM.SPECIAL'
              )}
            </ThemedText>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PasswordCheck;
